import { useEffect, useState } from 'react'
import { regionsApi } from '@carrent/api'
import { Icon } from '@carrent/ui'

/**
 * Загрузка списка регионов. Общая для всех компонентов файла.
 * Возвращает и состояние ошибки — прежняя версия глотала её молча
 * через .catch(() => {}), и пользователь видел пустой список без объяснения.
 */
function useRegions() {
  const [regions, setRegions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    regionsApi
      .list()
      .then((r) => alive && setRegions(r.data?.data || []))
      .catch(() => alive && setError('Не удалось загрузить регионы'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])

  return { regions, loading, error }
}

function LoadingRow() {
  return (
    <div className="region-loading">
      <span className="spinner spinner--sm" />
      Загрузка регионов…
    </div>
  )
}

/** Выбор одного региона. */
export function RegionPicker({ value, onChange, allowEmpty = false, label = 'Регион' }) {
  const { regions, loading, error } = useRegions()

  return (
    <div className="field">
      <label className="field__label" htmlFor="region-select">
        {label}
      </label>

      {loading && <LoadingRow />}
      {!loading && error && <p className="field__error">{error}</p>}

      {!loading && !error && (
        <select
          id="region-select"
          className="input"
          value={value || ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        >
          {allowEmpty && <option value="">— Без региона —</option>}
          {!allowEmpty && !value && <option value="">— Выберите регион —</option>}
          {regions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} ({r.code})
            </option>
          ))}
        </select>
      )}
    </div>
  )
}

/**
 * Множественный выбор регионов.
 *
 * Раньше был списком <button>, что неверно семантически: множественный
 * выбор — это чекбоксы. Скринридер объявлял их кнопками без состояния,
 * а с клавиатуры нельзя было понять, что выбрано.
 */
export function RegionsMultiPicker({ value = [], onChange, label = 'Регионы' }) {
  const { regions, loading, error } = useRegions()

  const toggle = (id) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])

  if (loading) return <LoadingRow />
  if (error) return <p className="field__error">{error}</p>

  if (regions.length === 0) {
    return (
      <div className="state">
        <p className="state__title">Регионов пока нет</p>
        <p className="state__text">Создайте регион, чтобы назначать его пользователям и машинам.</p>
      </div>
    )
  }

  return (
    <fieldset className="field region-fieldset">
      {label && <legend className="field__label">{label}</legend>}

      <div className="region-list">
        {regions.map((r) => {
          const active = value.includes(r.id)
          return (
            <label key={r.id} className={`region-option ${active ? 'is-active' : ''}`}>
              <input
                type="checkbox"
                className="visually-hidden"
                checked={active}
                onChange={() => toggle(r.id)}
              />
              <span className="region-option__box" aria-hidden="true">
                {active && <Icon name="check" size={12} />}
              </span>
              <span className="region-option__code">{r.code}</span>
              <span className="region-option__name">{r.name}</span>
            </label>
          )
        })}
      </div>

      {value.length === 0 && (
        <p className="field__hint field__hint--warn">
          Не выбрано ни одного региона — пользователь не увидит данных
        </p>
      )}
    </fieldset>
  )
}

/** Бейдж региона. */
export function RegionBadge({ region, size = 'sm' }) {
  if (!region) return <span className="region-badge region-badge--empty">без региона</span>

  return (
    <span className={`region-badge region-badge--${size}`}>
      <Icon name="map" size={size === 'lg' ? 13 : 11} />
      {region.code}
    </span>
  )
}

/** Список бейджей нескольких регионов. */
export function RegionBadges({ regions, max = 5 }) {
  if (!regions || regions.length === 0) {
    return <span className="region-badge region-badge--empty">без регионов</span>
  }

  const visible = regions.slice(0, max)
  const rest = regions.length - visible.length

  return (
    <div className="region-badges">
      {visible.map((r) => (
        <RegionBadge key={r.id} region={r} />
      ))}
      {rest > 0 && <span className="region-badges__rest">+{rest}</span>}
    </div>
  )
}
